/**
 * Firebase 초기화 모듈
 * 
 * 푸시 알림, Crashlytics 등 Firebase 서비스 사용을 위한 설정
 * 현재는 기본 초기화만 수행하며, 필요한 서비스는 추후 활성화
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAnalytics, Analytics, isSupported } from 'firebase/analytics';

// Firebase 설정 (환경변수에서 가져옴)
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Firebase 앱 인스턴스
let app: FirebaseApp | null = null;
let analytics: Analytics | null = null;

// Firebase 설정이 유효한지 확인
const isConfigValid = (): boolean => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.appId
  );
};

/**
 * Firebase 초기화
 * - 환경변수가 설정되지 않으면 초기화하지 않음
 * - Production 환경에서만 Analytics 활성화
 */
export async function initFirebase(): Promise<void> {
  if (!isConfigValid()) {
    console.log('[Firebase] Configuration not set - skipping initialization');
    return;
  }

  try {
    // Firebase 앱 초기화
    app = initializeApp(firebaseConfig);
    console.log('[Firebase] App initialized successfully');

    // Analytics 초기화 (브라우저 지원 확인)
    const analyticsSupported = await isSupported();
    if (analyticsSupported && import.meta.env.MODE === 'production') {
      analytics = getAnalytics(app);
      console.log('[Firebase] Analytics initialized');
    }
  } catch (error) {
    console.error('[Firebase] Initialization error:', error);
  }
}

/**
 * Firebase 앱 인스턴스 반환
 */
export function getFirebaseApp(): FirebaseApp | null {
  return app;
}

/**
 * Firebase Analytics 인스턴스 반환
 */
export function getFirebaseAnalytics(): Analytics | null {
  return analytics;
}

// ===========================================
// 아래는 추후 필요시 활성화할 서비스들
// ===========================================

/**
 * 푸시 알림 초기화 (추후 사용)
 * 
 * 사용 시 아래 import 추가 필요:
 * import { getMessaging, getToken, onMessage } from 'firebase/messaging';
 */
// export async function initPushNotifications(): Promise<string | null> {
//   if (!app) return null;
//   
//   try {
//     const messaging = getMessaging(app);
//     const token = await getToken(messaging, {
//       vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
//     });
//     console.log('[Firebase] Push notification token:', token);
//     return token;
//   } catch (error) {
//     console.error('[Firebase] Push notification error:', error);
//     return null;
//   }
// }

/**
 * Crashlytics 초기화 (추후 사용)
 * 
 * 웹에서는 별도 설정 필요 - Firebase Performance 사용 권장
 */
// export function initCrashlytics(): void {
//   // 웹용 Crashlytics 설정
// }
